import { Box, Typography, Alert, Link } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { apiService } from '../services/api';
import { API_ENDPOINTS } from '../constants/api';
import { formatDate } from '../utils/dateFormat';
import { useAuth } from '../contexts/AuthContext';
import type { ExchangeRateInfo } from '../hooks/useExchangeRates';
import { FlagIcon } from './FlagIcon';

interface ExchangeRateBannerProps {
  /** If provided, use this instead of fetching */
  rates?: ExchangeRateInfo | null;
  /** Optional margin bottom to match page layout */
  sx?: object;
}

/**
 * Banner showing exchange rates used for GBP conversion (reporting currency).
 * Styled to match Aardvark (MUI Card/Alert patterns). Shown on expense list pages.
 */
export function ExchangeRateBanner({ rates: ratesProp, sx }: ExchangeRateBannerProps) {
  const { displayTimeZoneId } = useAuth();
  const [rates, setRates] = useState<ExchangeRateInfo | null>(ratesProp ?? null);

  useEffect(() => {
    if (ratesProp !== undefined) {
      queueMicrotask(() => setRates(ratesProp));
      return;
    }
    let mounted = true;
    apiService
      .get<ExchangeRateInfo>(API_ENDPOINTS.SYSTEM.EXCHANGE_RATES)
      .then((res) => {
        if (mounted) setRates(res.data);
      })
      .catch(() => {
        if (mounted) setRates(null);
      });
    return () => {
      mounted = false;
    };
  }, [ratesProp]);

  const inverseZar = useMemo(() => {
    if (!rates || !rates.zarToGbpRate || rates.zarToGbpRate <= 0) return null;
    return 1 / rates.zarToGbpRate;
  }, [rates]);

  if (!rates) return null;

  const lastRefreshedText = formatDate(rates.lastRefreshed ?? null, { includeTime: true }) || 'Not available';

  return (
    <Alert
      severity="info"
      variant="outlined"
      key={displayTimeZoneId}
      sx={{
        mb: 2,
        '& .MuiAlert-message': { width: '100%' },
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 1 }}>
        <Typography variant="subtitle2" fontWeight="600" color="info.dark">
          Exchange rates
        </Typography>
        {rates.usingFallbackRates && (
          <Typography component="span" variant="caption" sx={{ px: 1, py: 0.5, bgcolor: 'warning.light', borderRadius: 1 }}>
            Fallback rates
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 3 }}>
        {inverseZar != null && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
              <FlagIcon code="GB" size={18} />
            </Box>
            <Typography variant="body2" color="text.secondary" component="span">
              1 GBP =
            </Typography>
            <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
              {inverseZar.toFixed(2)} ZAR
            </Typography>
            <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
              <FlagIcon code="ZA" size={18} />
            </Box>
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="body2" color="text.secondary" component="span">
            Last refreshed:
          </Typography>
          <Typography variant="body2" component="span">
            {lastRefreshedText}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="body2" color="text.secondary" component="span">
            Source:
          </Typography>
          <Link href={rates.sourceUrl} target="_blank" rel="noopener noreferrer" variant="body2" underline="hover">
            {rates.source}
          </Link>
        </Box>
      </Box>
    </Alert>
  );
}

export default ExchangeRateBanner;
